import { useTranslate } from "ra-core";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { TEMPERATURE_STYLE, isTemperature } from "./temperatureModel";

interface TemperatureBadgeProps {
  temperature?: string | null;
  reason?: string | null;
  isCorrected?: boolean | null;
  // Compact shows only the emoji (lists and deal cards).
  compact?: boolean;
  className?: string;
}

export const TemperatureBadge = ({
  temperature,
  reason,
  isCorrected,
  compact = false,
  className,
}: TemperatureBadgeProps) => {
  const translate = useTranslate();
  if (!isTemperature(temperature)) return null;

  const style = TEMPERATURE_STYLE[temperature];
  const label = translate(`hiper.temperature.values.${temperature}`);
  const badge = (
    <span
      role="img"
      aria-label={`${translate("hiper.temperature.title")}: ${label}`}
      data-temperature={temperature}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border text-xs font-medium whitespace-nowrap",
        compact ? "px-1 py-0" : "px-2 py-0.5",
        style.className,
        className,
      )}
    >
      <span aria-hidden="true">{style.emoji}</span>
      {compact ? null : <span>{label}</span>}
      {isCorrected ? <span aria-hidden="true">✎</span> : null}
    </span>
  );

  if (!reason) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          {label}: {reason}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
